/**
 * ⌘K / Ctrl-K — jump anywhere.
 *
 * With twenty-five tools, a sidebar is a map but not a shortcut. The palette
 * is the shortcut: type three letters, hit Enter. It searches labels,
 * descriptions and synonyms (so "shepardize" finds the Citator and "diff"
 * finds Redline), ranks prefix matches first, and remembers the last few
 * destinations so the common path is the top of an empty query.
 *
 * Keyboard: ↑/↓ move, Enter opens, Esc closes. The list is an ARIA listbox and
 * the input owns focus throughout, which is what screen readers expect from a
 * combobox — no roving tabindex needed.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { logger } from '@/lib/logger';
import { cx } from '@/lib/cx';
import { Icon } from './icons';
import { ADMIN_NAV, TOOL_GROUPS, UTILITY_NAV, type NavItem } from './nav';

const RECENTS_KEY = 'alfie:palette-recents';
const MAX_RECENTS = 5;
const log = logger.child('palette');

interface Entry extends NavItem {
  group: string;
}

function readRecents(): string[] {
  try {
    const raw = window.localStorage.getItem(RECENTS_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

function writeRecents(paths: string[]): void {
  try {
    window.localStorage.setItem(RECENTS_KEY, JSON.stringify(paths.slice(0, MAX_RECENTS)));
  } catch {
    /* storage unavailable — recents are a convenience, not state we need */
  }
}

/**
 * Rank an entry against the query. Higher is better; 0 means "no match".
 * A label prefix beats a label substring, which beats a keyword or description
 * hit — so typing "cit" puts Citator above Cite-Check's description.
 */
export function scoreEntry(entry: Entry, query: string): number {
  const q = query.trim().toLowerCase();
  if (!q) return 1;
  const label = entry.label.toLowerCase();
  if (label.startsWith(q)) return 100 - label.length;
  if (label.includes(q)) return 60 - label.length;
  if (entry.keywords?.some((k) => k.toLowerCase().startsWith(q))) return 50;
  if (entry.keywords?.some((k) => k.toLowerCase().includes(q))) return 40;
  if (entry.group.toLowerCase().includes(q)) return 25;
  if (entry.description?.toLowerCase().includes(q)) return 20;
  // Last resort: initials, so "bd" reaches the Brief Deconstructor.
  const initials = entry.label.split(/[\s/]+/).map((w) => w[0]?.toLowerCase() ?? '').join('');
  return initials.startsWith(q) ? 30 : 0;
}

/** Wraps the matched span of `text` in <mark>, case-insensitively. */
function Highlight({ text, query }: { text: string; query: string }) {
  const q = query.trim();
  if (!q) return <>{text}</>;
  const at = text.toLowerCase().indexOf(q.toLowerCase());
  if (at < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, at)}
      <mark>{text.slice(at, at + q.length)}</mark>
      {text.slice(at + q.length)}
    </>
  );
}

export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const { status, isAdmin, isStaff } = useAuth();
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const [recents, setRecents] = useState<string[]>(readRecents);
  const listRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const authed = status === 'authenticated';

  // Everything the caller may actually reach, with its group for the label.
  const entries = useMemo<Entry[]>(() => {
    const tools = TOOL_GROUPS.flatMap((g) => g.items.map((i) => ({ ...i, group: g.label })));
    const admin = ADMIN_NAV.filter((i) => (i.admin ? isAdmin : i.staff ? isStaff : true)).map((i) => ({ ...i, group: 'Admin' }));
    const utility = UTILITY_NAV.map((i) => ({ ...i, group: 'Account' }));
    return [...tools, ...admin, ...utility].filter((i) => authed || !i.gated);
  }, [authed, isAdmin, isStaff]);

  const results = useMemo(() => {
    const scored = entries
      .map((e) => ({ e, score: scoreEntry(e, query) }))
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score);
    if (query.trim()) return scored.slice(0, 12).map((x) => x.e);
    // Empty query: the last places you went, then the rest in nav order.
    const byPath = new Map(entries.map((e) => [e.to, e]));
    const recent = recents.map((p) => byPath.get(p)).filter((e): e is Entry => Boolean(e));
    const rest = entries.filter((e) => !recents.includes(e.to));
    return [...recent, ...rest].slice(0, 12);
  }, [entries, query, recents]);

  useEffect(() => {
    setCursor(0);
  }, [query]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setRecents(readRecents());
      // Focus after paint so the browser does not scroll the page behind us.
      const id = window.requestAnimationFrame(() => inputRef.current?.focus());
      return () => window.cancelAnimationFrame(id);
    }
    return undefined;
  }, [open]);

  // Escape closes from anywhere while open — not only once focus has reached the input,
  // which happens a frame after opening (a quick Escape used to be ignored).
  useEffect(() => {
    if (!open) return undefined;
    const onEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onEscape);
    return () => window.removeEventListener('keydown', onEscape);
  }, [open, onClose]);

  // Keep the highlighted row in view while arrowing through a long list.
  // `scrollIntoView` is absent in jsdom and in a few older engines, so this is
  // a best-effort nicety rather than something the palette depends on.
  useEffect(() => {
    const row = listRef.current?.querySelector('[aria-selected="true"]');
    if (row && typeof row.scrollIntoView === 'function') row.scrollIntoView({ block: 'nearest' });
  }, [cursor, results]);

  const go = useCallback(
    (entry: Entry) => {
      const next = [entry.to, ...recents.filter((p) => p !== entry.to)].slice(0, MAX_RECENTS);
      setRecents(next);
      writeRecents(next);
      log.info('Navigated from the palette', { to: entry.to, query: query.trim() || undefined });
      onClose();
      navigate(entry.to);
    },
    [navigate, onClose, query, recents],
  );

  if (!open) return null;

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown' || (e.key === 'n' && e.ctrlKey)) {
      e.preventDefault();
      setCursor((c) => (results.length ? (c + 1) % results.length : 0));
    } else if (e.key === 'ArrowUp' || (e.key === 'p' && e.ctrlKey)) {
      e.preventDefault();
      setCursor((c) => (results.length ? (c - 1 + results.length) % results.length : 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const chosen = results[cursor];
      if (chosen) go(chosen);
    }
  }

  return (
    <div className="palette-scrim" onClick={onClose} role="presentation">
      <div
        className="palette"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
      >
        <div className="palette-input">
          <Icon.Search aria-hidden />
          <input
            ref={inputRef}
            type="text"
            value={query}
            placeholder="Search tools and pages…"
            aria-label="Search tools and pages"
            role="combobox"
            aria-expanded
            aria-controls="palette-results"
            aria-activedescendant={results[cursor] ? `palette-${cursor}` : undefined}
            autoComplete="off"
            spellCheck={false}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        <div className="palette-results" id="palette-results" role="listbox" aria-label="Results" ref={listRef}>
          {results.length === 0 ? (
            <div className="empty compact">
              <span className="empty-title">No matches</span>
              <span className="empty-hint">Try a tool name, or what you want to do — “compare”, “deadline”, “cite”.</span>
            </div>
          ) : (
            results.map((entry, i) => {
              const Glyph = Icon[entry.icon];
              return (
                <button
                  key={entry.to}
                  id={`palette-${i}`}
                  type="button"
                  role="option"
                  aria-selected={i === cursor}
                  className={cx('palette-item')}
                  onMouseMove={() => setCursor(i)}
                  onClick={() => go(entry)}
                >
                  <Glyph aria-hidden />
                  <span className="stack grow">
                    <span className="fw-semibold"><Highlight text={entry.label} query={query} /></span>
                    {entry.description && <span className="t-xs muted truncate">{entry.description}</span>}
                  </span>
                  <span className="group">{entry.group}</span>
                </button>
              );
            })
          )}
        </div>

        <div className="palette-foot">
          <span className="hint"><kbd className="kbd">↑</kbd><kbd className="kbd">↓</kbd> navigate</span>
          <span className="hint"><kbd className="kbd">↵</kbd> open</span>
          <span className="hint"><kbd className="kbd">esc</kbd> close</span>
          {!authed && <span className="right muted-more">Sign in to reach every tool</span>}
        </div>
      </div>
    </div>
  );
}

/**
 * Owns the ⌘K / Ctrl-K shortcut and the open state, so `Layout` stays
 * declarative. The shortcut is ignored while the user is typing somewhere else
 * — except for the palette itself, which toggles closed.
 */
export function useCommandPalette() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return { open, setOpen, close: useCallback(() => setOpen(false), []) };
}
